import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import sanitizeHtml from 'sanitize-html';
import {
  Box,
  Button,
  IconButton,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { InfoRounded, LockRounded } from '@mui/icons-material';
import NotificationDialogV2 from '@src/components/NotificationDialogV2';
import { FREE_PACKAGE_CODES } from '@src/constants/package';
import { checkCustomPackage } from '@src/services/package';
import theme from '@src/styles/theme';
import { delimitNumber } from '@src/utils/number';
import { StyledRemainingCredits } from './index.style';

const POPUP_CREDIT_TYPE = {
  FREE_PACKAGE: 'FREE_PACKAGE',
  REFERRAL_CREDIT: 'REFERRAL_CREDIT',
  LOCK_CYCLE_CREDITS: 'LOCK_CYCLE_CREDITS',
};

const RemainingCredits = ({ credits, isLocked, referralCredits }) => {
  const { t, i18n } = useTranslation();
  const { language } = i18n;
  const { usingPackage } = useSelector((state) => state.user);
  const { user: userInfo } = useSelector((state) => state.auth);
  const isMobile = useMediaQuery(theme.breakpoints.down('smd'));
  const [showCreditInfo, setShowCreditInfo] = useState(false);
  const [popupCreditType, setPopupCreditType] = useState(null);

  const isUsingCustomPackage = checkCustomPackage({
    type: usingPackage?.type,
    level: usingPackage?.level,
  });

  const {
    introductoryDailyCredits,
    introductoryPeriodDays,
    regularDailyCredits,
  } = userInfo?.studioUsageOptions?.freemiumConfig || {};
  const isFreePackage = FREE_PACKAGE_CODES.includes(usingPackage?.code);
  const showFreePackageTooltip =
    regularDailyCredits !== introductoryDailyCredits;

  const showReferralCreditTooltip = !!referralCredits;

  const handleClickShowCreditInfo = (type) => {
    if (!isMobile) return;
    setPopupCreditType(type);
    setShowCreditInfo(true);
  };

  const renderButtonGotIt = (handleClick) => (
    <div className="used-credits-button-container">
      <Button
        className="button-got-it"
        variant="contained"
        onClick={handleClick}
      >
        {t('gotItV2')}
      </Button>
    </div>
  );

  const renderPopupDescription = () => {
    switch (popupCreditType) {
      case POPUP_CREDIT_TYPE.FREE_PACKAGE:
        return t('introductoryPeriodDays', {
          introductoryPeriodDays,
          introductoryDailyCredits:
            introductoryDailyCredits?.toLocaleString(language),
          regularDailyCredits: regularDailyCredits?.toLocaleString(language),
        });
      case POPUP_CREDIT_TYPE.REFERRAL_CREDIT:
        return `${t('includes')} ${t('rewardCharacters', {
          capturedReferralCreditsCycle: referralCredits,
        })}`;
      case POPUP_CREDIT_TYPE.LOCK_CYCLE_CREDITS:
        return t('lockCycleCreditsTooltipMyPackage');
      default:
        return '';
    }
  };

  const renderLockIcon = () => {
    if (isUsingCustomPackage)
      return (
        <Tooltip
          arrow
          className="tootlip"
          title={t('lockCycleCreditsTooltipMyPackage')}
          placement="left"
        >
          <IconButton
            onClick={() =>
              handleClickShowCreditInfo(POPUP_CREDIT_TYPE.LOCK_CYCLE_CREDITS)
            }
          >
            <LockRounded sx={{ fontSize: 16 }} className="lock-icon" />
          </IconButton>
        </Tooltip>
      );
    return <LockRounded sx={{ fontSize: 16 }} className="lock-icon" />;
  };

  return (
    <StyledRemainingCredits>
      <Box className="remaining-credits-container">
        <Typography className="label">{t('remainingCharacters')}</Typography>
        <Box className="remaining-content-container">
          {!!isLocked && renderLockIcon()}
          <div className="remaining-credits-container">
            <Typography className="content">{credits}</Typography>
            {isFreePackage && showFreePackageTooltip && (
              <Tooltip
                arrow
                className="tooltip"
                title={
                  <Typography
                    component="span"
                    sx={{ fontSize: '12px' }}
                    dangerouslySetInnerHTML={{
                      __html: sanitizeHtml(
                        t('introductoryPeriodDays', {
                          introductoryPeriodDays,
                          introductoryDailyCredits:
                            introductoryDailyCredits?.toLocaleString(language),
                          regularDailyCredits:
                            regularDailyCredits?.toLocaleString(language),
                        }),
                      ),
                    }}
                  />
                }
                placement="left"
              >
                <IconButton
                  onClick={() =>
                    handleClickShowCreditInfo(POPUP_CREDIT_TYPE.FREE_PACKAGE)
                  }
                >
                  <InfoRounded color="gray" className="icon" fontSize="16px" />
                </IconButton>
              </Tooltip>
            )}

            {showReferralCreditTooltip && (
              <Tooltip
                arrow
                className="tooltip"
                title={
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '8px',
                    }}
                  >
                    <Typography sx={{ fontSize: '14px' }}>
                      {`${t('includes')} ${t('rewardCharacters', {
                        capturedReferralCreditsCycle: delimitNumber(
                          referralCredits,
                          language,
                        ),
                      })}.`}
                    </Typography>
                  </Box>
                }
                placement="left"
              >
                <IconButton
                  onClick={() =>
                    handleClickShowCreditInfo(POPUP_CREDIT_TYPE.REFERRAL_CREDIT)
                  }
                >
                  <InfoRounded color="gray" className="icon" fontSize="16px" />
                </IconButton>
              </Tooltip>
            )}
          </div>
        </Box>

        <NotificationDialogV2
          showIcon={false}
          title={t('detailInfo')}
          description={renderPopupDescription()}
          open={showCreditInfo}
          onClose={() => setShowCreditInfo(false)}
          actionComponent={renderButtonGotIt(() => setShowCreditInfo(false))}
        />
      </Box>
    </StyledRemainingCredits>
  );
};

export default RemainingCredits;
